import { useState } from 'react';
import { X, Loader2, Plus, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePets } from '../context/PetsContext';
import { useAuth } from '../../../context/AuthContext';
import type { Pet } from '../types/pet';
import type { CreatePetRequest } from '../../../api/pets';
import { DOG_BREED_LABELS, CAT_BREED_LABELS } from '../data/breeds';
import { toast } from 'sonner';

const MAX_PETS = 20;

export interface QuickPetInput {
  name: string;
  species: 'dog' | 'cat';
  breed: string;
  dateOfBirth: string;
  approximateAge: string;
  gender: 'male' | 'female';
  neuteredSpayed: 'yes' | 'no';
}

const emptyPetInput = (): QuickPetInput => ({
  name: '',
  species: 'dog',
  breed: '',
  dateOfBirth: '',
  approximateAge: '',
  gender: 'male',
  neuteredSpayed: 'no',
});

interface QuickAddPetsModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export function QuickAddPetsModal({
  onClose,
  onSuccess,
}: QuickAddPetsModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addPetToApi, addPet, refreshPets } = usePets();
  const [step, setStep] = useState<1 | 2>(1);
  const [numberOfPets, setNumberOfPets] = useState(1);
  const [pets, setPets] = useState<QuickPetInput[]>(() => [emptyPetInput()]);
  const [submitting, setSubmitting] = useState(false);
  const [, setSuccessCount] = useState(0);
  const [showAddMore, setShowAddMore] = useState(false);

  const syncPetsToCount = (n: number) => {
    const clamped = Math.min(MAX_PETS, Math.max(1, n));
    setNumberOfPets(clamped);
    setPets((prev) => {
      if (prev.length === clamped) return prev;
      if (prev.length > clamped) return prev.slice(0, clamped);
      const next = [...prev];
      for (let i = prev.length; i < clamped; i++) {
        next.push(emptyPetInput());
      }
      return next;
    });
  };

  const updatePet = (index: number, updates: Partial<QuickPetInput>) => {
    setPets((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...updates } : p))
    );
  };

  const validateStep2 = (): string[] => {
    const errors: string[] = [];
    pets.forEach((p, i) => {
      if (!p.name.trim())
        errors.push(
          `Pet #${i + 1}: ${t('validation.nameRequired', 'Name is required')}`
        );
      if (!p.breed.trim())
        errors.push(
          `Pet #${i + 1}: ${t('validation.breedRequired', 'Breed is required')}`
        );
      if (!p.dateOfBirth && !p.approximateAge.trim()) {
        errors.push(
          `Pet #${i + 1}: ${t('validation.dobOrAgeRequired', 'Date of birth or approximate age is required')}`
        );
      }
    });
    return errors;
  };

  const toCreatePetRequest = (p: QuickPetInput): CreatePetRequest => ({
    name: p.name.trim(),
    species: p.species,
    breed: p.breed.trim(),
    dateOfBirth: p.dateOfBirth || undefined,
    approximateAge: p.approximateAge.trim() || undefined,
    gender: p.gender,
    neuteredSpayed: p.neuteredSpayed === 'yes',
    shareHealthData: true,
    shareGroomingNotes: true,
    emergencyAccess: true,
  });

  const handleCreate = async () => {
    const errs = validateStep2();
    if (errs.length > 0) {
      toast.error(errs[0]);
      return;
    }
    setSubmitting(true);
    let created = 0;
    try {
      for (let i = 0; i < pets.length; i++) {
        const p = pets[i];
        const payload = toCreatePetRequest(p);
        const { petId } = await addPetToApi(payload);
        const newPet: Pet = {
          id: petId,
          did: '',
          name: p.name.trim(),
          species: p.species,
          breed: p.breed.trim(),
          dateOfBirth: p.dateOfBirth || p.approximateAge,
          gender: p.gender,
          color: '',
          microchipId: '',
          owner: {
            name: user?.name || '',
            email: user?.email || '',
          },
        };
        addPet(newPet);
        created++;
      }
      await refreshPets();
      setSuccessCount(created);
      setShowAddMore(true);

      // Award "First Pet Added" reward (silently, non-blocking)
      if (created > 0) {
        try {
          const { completeEarningAction } = await import('@/api/rewards');
          await completeEarningAction('r1773697962473');
        } catch { /* Already completed or action not found — ignore */ }
      }
      toast.success(
        t(
          'quickAddPets.success',
          '{{count}} pet(s) added. You can edit full profile from Overview.',
          { count: created }
        )
      );
    } catch (e) {
      console.error(e);
      toast.error(
        t('quickAddPets.error', 'Failed to add some pets. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMore = () => {
    setShowAddMore(false);
    setSuccessCount(0);
    setStep(1);
    setNumberOfPets(1);
    setPets([emptyPetInput()]);
  };

  if (showAddMore) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl border dark:border-white/8 max-w-sm w-full p-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-6">
            <Users className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('quickAddPets.doneTitle', 'Pets added')}
          </h3>
          <p className="text-gray-600 dark:text-white/70 text-sm mb-6">
            {t(
              'quickAddPets.doneMessage',
              'You can edit full profile (ownership, health, lifestyle) for each pet from Overview → Edit profile.'
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <button
              type="button"
              onClick={handleAddMore}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              {t('quickAddPets.addMore', 'Add more pets')}
            </button>
            <button
              type="button"
              onClick={() => {
                onSuccess?.();
                onClose();
              }}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium border-2 border-gray-300 dark:border-white/20 text-gray-700 dark:text-white/90 hover:bg-gray-50 dark:hover:bg-white/5"
            >
              {t('common.done', 'Done')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl border dark:border-white/8 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-blue-500/90 to-purple-500/90 dark:from-blue-600 dark:to-purple-600 text-white p-6 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Users className="w-7 h-7" />
              <div>
                <h2 className="text-xl font-bold">
                  {step === 1
                    ? t('quickAddPets.title', 'Quick add multiple pets')
                    : t('quickAddPets.step2Title', 'Pet details')}
                </h2>
                <p className="text-sm opacity-90">
                  {step === 1
                    ? t(
                        'quickAddPets.step1Subtitle',
                        'Mandatory fields only. Edit full profile later from Overview.'
                      )
                    : t(
                        'quickAddPets.step2Subtitle',
                        'Fill mandatory fields for each pet.'
                      )}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white/80 hover:text-white p-1 rounded"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1">
                  {t(
                    'quickAddPets.howMany',
                    'How many pets do you want to add?'
                  )}{' '}
                  *
                </label>
                <input
                  type="number"
                  min={1}
                  max={MAX_PETS}
                  value={numberOfPets}
                  onChange={(e) =>
                    syncPetsToCount(parseInt(e.target.value, 10) || 1)
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 border border-gray-300 dark:border-white/20 rounded-lg text-gray-700 dark:text-white/90 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium hover:opacity-90 cursor-pointer"
                >
                  {t('common.next', 'Next')}
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-white/70">
                {t(
                  'quickAddPets.editLater',
                  'You can add ownership, health, lifestyle and photo later from each pet’s Overview → Edit profile.'
                )}
              </p>
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                {pets.map((pet, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/5 space-y-3"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">
                      {t('quickAddPets.petNumber', 'Pet')} #{index + 1}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                          {t('quickAddPets.name', 'Name')} *
                        </label>
                        <input
                          type="text"
                          value={pet.name}
                          onChange={(e) =>
                            updatePet(index, { name: e.target.value })
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                          placeholder={t(
                            'quickAddPets.namePlaceholder',
                            'Pet name'
                          )}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                          {t('quickAddPets.species', 'Species')} *
                        </label>
                        <select
                          value={pet.species}
                          onChange={(e) => {
                            const species = e.target.value as 'dog' | 'cat';
                            updatePet(index, {
                              species,
                              breed: '',
                            });
                          }}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                        >
                          <option value="dog">
                            {t('petOnboarding.step1.dog', 'Dog')}
                          </option>
                          <option value="cat">
                            {t('petOnboarding.step1.cat', 'Cat')}
                          </option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                          {t('quickAddPets.breed', 'Breed')} *
                        </label>
                        <input
                          type="text"
                          value={pet.breed}
                          onChange={(e) =>
                            updatePet(index, { breed: e.target.value })
                          }
                          list={`quick-breed-${index}`}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                          placeholder={t(
                            'quickAddPets.breedPlaceholder',
                            'Breed'
                          )}
                        />
                        <datalist id={`quick-breed-${index}`}>
                          {(pet.species === 'cat'
                            ? CAT_BREED_LABELS
                            : DOG_BREED_LABELS
                          ).map((label, i) => (
                            <option key={i} value={label} />
                          ))}
                        </datalist>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                          {t('quickAddPets.gender', 'Gender')} *
                        </label>
                        <select
                          value={pet.gender}
                          onChange={(e) =>
                            updatePet(index, {
                              gender: e.target.value as 'male' | 'female',
                            })
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                        >
                          <option value="male">
                            {t('petOnboarding.step1.male', 'Male')}
                          </option>
                          <option value="female">
                            {t('petOnboarding.step1.female', 'Female')}
                          </option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                          {t('quickAddPets.dateOfBirth', 'Date of birth')}
                        </label>
                        <input
                          type="date"
                          value={pet.dateOfBirth}
                          onChange={(e) =>
                            updatePet(index, { dateOfBirth: e.target.value })
                          }
                          max={new Date().toISOString().slice(0, 10)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                          {t(
                            'quickAddPets.approximateAge',
                            'Or approximate age'
                          )}
                        </label>
                        <input
                          type="text"
                          value={pet.approximateAge}
                          onChange={(e) =>
                            updatePet(index, {
                              approximateAge: e.target.value,
                            })
                          }
                          disabled={!!pet.dateOfBirth}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white disabled:opacity-60"
                          placeholder="e.g. 2 years"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                        {t('quickAddPets.neuteredSpayed', 'Neutered / Spayed')}{' '}
                        *
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`neutered-${index}`}
                            checked={pet.neuteredSpayed === 'yes'}
                            onChange={() =>
                              updatePet(index, { neuteredSpayed: 'yes' })
                            }
                            className="rounded-full"
                          />
                          <span className="text-sm dark:text-white/90">
                            {t('common.yes', 'Yes')}
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`neutered-${index}`}
                            checked={pet.neuteredSpayed === 'no'}
                            onChange={() =>
                              updatePet(index, { neuteredSpayed: 'no' })
                            }
                            className="rounded-full"
                          />
                          <span className="text-sm dark:text-white/90">
                            {t('common.no', 'No')}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-2.5 border border-gray-300 dark:border-white/20 rounded-lg text-gray-700 dark:text-white/90 hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  {t('common.back', 'Back')}
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={submitting}
                  className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium hover:opacity-90 disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('common.saving', 'Saving...')}
                    </>
                  ) : (
                    t('quickAddPets.createPets', 'Create {{count}} pet(s)', {
                      count: pets.length,
                    })
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
